import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  User, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export default function PublicCancel() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState(false);
  const [refundedAmount, setRefundedAmount] = useState(0);

  const formatCurrency = (value, currency = 'EUR') => {
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: String(currency || 'EUR').toUpperCase(),
      }).format(Number(value || 0));
    } catch {
      return `${Number(value || 0).toFixed(2)} ${String(currency || 'EUR').toUpperCase()}`;
    }
  };

  const loadAppointment = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicAPI.getAppointmentInfo(appointmentId);
      setAppointment(res.data);
    } catch (error) {
      console.error('Failed to load appointment:', error);
      toast.error('Termin konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    if (appointmentId) {
      loadAppointment();
    }
  }, [appointmentId, loadAppointment]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await publicAPI.cancelAppointment(appointmentId, { reason });
      const refundAmount = Number(response.data?.refund?.refunded_amount || 0);
      setRefundedAmount(refundAmount);
      setSuccess(true);
      toast.success(
        refundAmount > 0
          ? `Termin storniert und ${formatCurrency(refundAmount, appointment?.currency)} zurückerstattet.`
          : 'Termin erfolgreich storniert'
      );
    } catch (error) {
      console.error('Cancellation failed:', error);
      toast.error(error.response?.data?.detail || 'Stornierung fehlgeschlagen');
    } finally {
      setCancelling(false);
    }
  };

  const handleGoToBooking = () => {
    if (appointment?.org_slug) {
      navigate(`/book/${appointment.org_slug}`);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!appointment && !loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-sm">
          <XCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">Termin nicht gefunden</h1>
          <p className="text-[#86868B] mb-6">Der Link ist ungültig oder der Termin wurde bereits gelöscht.</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full py-4 bg-[#1D1D1F] text-white rounded-2xl font-semibold hover:bg-black transition-colors"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  if (success || appointment.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-sm border border-[#E5E5EA]">
          <div className="w-16 h-16 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#34C759]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-3">Termin storniert</h1>
          <p className="text-[#86868B] mb-8">
            Dein Termin bei <strong>{appointment.org_name}</strong> wurde erfolgreich storniert.
          </p>
          {refundedAmount > 0 && (
            <div className="mb-8 rounded-2xl border border-[#34C759]/15 bg-[#EAF8EF] p-4 text-sm text-[#16833A]">
              <strong>{formatCurrency(refundedAmount, appointment.currency)}</strong> wurden automatisch über deine ursprüngliche Zahlungsart zurückerstattet.
            </div>
          )}
          
          <button 
            onClick={handleGoToBooking}
            className="w-full py-4 bg-[#1D1D1F] text-white rounded-2xl font-semibold hover:bg-black transition-all flex items-center justify-center gap-2"
          >
            Neuen Termin buchen
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const startDate = new Date(appointment.start_time);
  const isCancellationBlocked = appointment.can_cancel === false;
  const isPaidCancellation = appointment.cancel_block_reason === 'paid';
  const automaticRefundAmount = Number(appointment.automatic_refund_amount || 0);

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="max-w-lg w-full">
        {/* Header/Logo section could go here if we had org logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#1D1D1F] mb-2">Termin stornieren</h1>
          <p className="text-[#86868B]">Möchtest du deinen Termin wirklich absagen?</p>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-[#E5E5EA] overflow-hidden mb-6">
          <div className="p-6 sm:p-8 border-b border-[#F5F5F7]">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#F5F5F7] rounded-2xl flex items-center justify-center shrink-0">
                <Calendar className="w-6 h-6 text-[#1D1D1F]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#86868B] uppercase tracking-wider mb-1">Deine Buchung</div>
                <div className="text-lg font-bold text-[#1D1D1F] leading-tight">{appointment.service_name}</div>
                <div className="text-[#86868B] mt-1">bei {appointment.org_name}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 text-[#1D1D1F]">
                <Clock className="w-4 h-4 text-[#86868B]" />
                <span className="font-medium">
                  {format(startDate, 'EEEE, d. MMMM', { locale: de })}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[#1D1D1F]">
                <Clock className="w-4 h-4 text-[#86868B]" />
                <span className="font-medium">
                  {format(startDate, 'HH:mm')} Uhr
                </span>
              </div>
            </div>
            {appointment.location_name && (
              <div className="mt-4 flex items-start gap-3 text-[#86868B] text-sm">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{appointment.location_name}</span>
              </div>
            )}
            {Number(appointment.paid_amount || 0) > 0 && (
              <div className="mt-4 rounded-2xl bg-[#EAF8EF] border border-[#34C759]/15 p-4 text-sm text-[#16833A]">
                Bezahlt: <strong>{formatCurrency(appointment.paid_amount, appointment.currency)}</strong>
              </div>
            )}
            {!isCancellationBlocked && automaticRefundAmount > 0 && (
              <div className="mt-3 rounded-2xl border border-[#34C759]/15 bg-[#EAF8EF] p-4 text-sm text-[#16833A]">
                Bei der Stornierung werden <strong>{formatCurrency(automaticRefundAmount, appointment.currency)}</strong> automatisch über die ursprüngliche Zahlungsart zurückerstattet.
              </div>
            )}
          </div>

          <div className="p-6 sm:p-8 bg-[#FAFAFB]">
            {isCancellationBlocked ? (
              <div className="flex items-start gap-3 p-4 bg-[#FFF4E5] rounded-2xl border border-[#FF9500]/15">
                <AlertTriangle className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
                <div className="text-sm text-[#7A4E00] leading-relaxed">
                  <p className="font-semibold text-[#7A4E00] mb-1">
                    {isPaidCancellation ? 'Online-Stornierung wegen Zahlung nicht möglich' : 'Online-Stornierung nicht möglich'}
                  </p>
                  <p>
                    {appointment.cancel_block_message || (
                      isPaidCancellation
                        ? 'Dieser Termin wurde bereits bezahlt. Bitte kontaktiere den Standort, damit die Stornierung und eine mögliche Rückerstattung korrekt geprüft werden können.'
                        : 'Bitte kontaktiere den Standort, um diesen Termin zu stornieren.'
                    )}
                  </p>
                  {(appointment.org_email || appointment.org_phone) && (
                    <div className="mt-3 space-y-1">
                      {appointment.org_email && (
                        <a className="block font-medium underline" href={`mailto:${appointment.org_email}`}>
                          {appointment.org_email}
                        </a>
                      )}
                      {appointment.org_phone && (
                        <a className="block font-medium underline" href={`tel:${appointment.org_phone}`}>
                          {appointment.org_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <label htmlFor="reason" className="block text-sm font-semibold text-[#1D1D1F] mb-3">
                  Grund für die Stornierung (optional)
                </label>
                <textarea
                  id="reason"
                  rows={4}
                  className="w-full p-4 rounded-2xl border border-[#E5E5EA] bg-white text-[#1D1D1F] placeholder-[#C7C7CC] focus:ring-2 focus:ring-[#1D1D1F]/5 focus:border-[#1D1D1F] transition-all outline-none resize-none"
                  placeholder="z.B. Terminüberschneidung, Krankheit..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                
                <div className="mt-6 flex items-start gap-3 p-4 bg-[#FFF2F2] rounded-2xl border border-[#FF3B30]/10">
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#FF3B30] leading-relaxed">
                    Diese Aktion kann nicht rückgängig gemacht werden. Der Termin wird sofort freigegeben.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="p-6 sm:p-8 flex flex-col gap-3">
            {!isCancellationBlocked && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className={`w-full py-4 rounded-2xl font-bold text-white transition-all shadow-sm ${
                  cancelling ? 'bg-[#1D1D1F]/50 cursor-not-allowed' : 'bg-[#FF3B30] hover:bg-[#D70015]'
                }`}
              >
                {cancelling ? 'Wird storniert...' : 'Termin jetzt stornieren'}
              </button>
            )}
            <button
              onClick={() => window.history.back()}
              disabled={cancelling}
              className="w-full py-4 rounded-2xl font-semibold text-[#1D1D1F] hover:bg-[#F5F5F7] transition-all"
            >
              Doch nicht stornieren
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-[#86868B] px-4">
          Es gelten die Stornierungsbedingungen von {appointment.org_name}. 
          Bei kurzfristigen Absagen können Gebühren anfallen.
        </p>
      </div>
    </div>
  );
}
